import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { LANGUAGES, translations } from "@/i18n/translations";

const I18nContext = createContext(null);
const STORAGE_KEY = "va_lang";

function detectLang() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && translations[saved]) return saved;
  const nav = (navigator.language || "fr").slice(0, 2).toLowerCase();
  return translations[nav] ? nav : "fr";
}

function resolve(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] != null ? acc[k] : undefined), obj);
}

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectLang);

  const dir = useMemo(() => LANGUAGES.find((l) => l.code === lang)?.dir || "ltr", [lang]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", dir);
  }, [lang, dir]);

  const setLang = useCallback((code) => {
    if (translations[code]) setLangState(code);
  }, []);

  const t = useCallback(
    (key, vars) => {
      let val = resolve(translations[lang], key);
      if (val === undefined) val = resolve(translations.fr, key);
      if (val === undefined) return key;
      if (vars && typeof val === "string") {
        Object.entries(vars).forEach(([k, v]) => {
          val = val.replace(new RegExp(`\\{${k}\\}`, "g"), v);
        });
      }
      return val;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, dir, languages: LANGUAGES }), [lang, setLang, t, dir]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
